// "New alarms are available for your boat" (TOM-847): after a core update
// brings alarm types the boat can arm, or new readings make some possible, the
// Alarms page says so and links to Suggest alarms on the boat's equipment. It
// never adds or arms anything: the owner picks on that page. Dismissing it, or
// asking the boat there, clears it (api_contracts.md §5.10).

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { api } from '../../api';

export interface NewAlarmType {
  recipeId: string;
  title: string;
}

export const NEW_ALARMS_HEADLINE = 'New alarms are available for your boat';

/** "Person seen, Alarm sound heard and Camera offline", or the first three and a count. */
export function newAlarmsLine(types: readonly NewAlarmType[]): string {
  const names = types.map((t) => t.title);
  if (names.length <= 1) return names.join('');
  if (names.length <= 3) return `${names.slice(0, -1).join(', ')} and ${names.at(-1)}`;
  return `${names.slice(0, 3).join(', ')} and ${names.length - 3} more`;
}

/** The types in a `new-available` answer; one without a list (an older cloud, a stub) is nothing new. */
export function newAlarmTypes(res: { recipes?: unknown } | null | undefined): NewAlarmType[] {
  return Array.isArray(res?.recipes) ? (res.recipes as NewAlarmType[]) : [];
}

export function NewAlarmsNoteView({
  boatId,
  types,
  onDismiss,
  dismissing = false,
}: {
  boatId: string;
  types: readonly NewAlarmType[];
  onDismiss: () => void;
  dismissing?: boolean;
}) {
  if (types.length === 0) return null;
  return (
    <section className="al-newalarms al-card" role="status" data-testid="alarms-new-available">
      <div className="al-newalarms__text">
        <strong className="al-newalarms__headline">{NEW_ALARMS_HEADLINE}</strong>
        <span className="al-newalarms__list">{newAlarmsLine(types)}</span>
      </div>
      <div className="al-newalarms__actions">
        <Link
          className="tb-btn tb-btn--tinted tb-btn--sm"
          to={`/dashboard/boats/${boatId}/alarms/regenerate`}
          data-testid="alarms-new-available-suggest"
        >
          Suggest alarms
        </Link>
        <button
          type="button"
          className="al-textbtn"
          onClick={onDismiss}
          disabled={dismissing}
          data-testid="alarms-new-available-dismiss"
        >
          Dismiss
        </button>
      </div>
    </section>
  );
}

/** The note, read once per visit; a failed read shows nothing. */
export function NewAlarmsNote({ boatId }: { boatId: string }) {
  const [types, setTypes] = useState<NewAlarmType[]>([]);
  const [dismissing, setDismissing] = useState(false);
  useEffect(() => {
    let alive = true;
    void api<{ recipes: NewAlarmType[] }>(`/v1/boats/${boatId}/alarms/new-available`).then(
      (res) => {
        if (alive) setTypes(newAlarmTypes(res));
      },
      () => undefined
    );
    return () => {
      alive = false;
    };
  }, [boatId]);
  const dismiss = useCallback(() => {
    setDismissing(true);
    void api(`/v1/boats/${boatId}/alarms/new-available/dismiss`, { method: 'POST' }).then(
      () => setTypes([]),
      () => setDismissing(false)
    );
  }, [boatId]);
  return (
    <NewAlarmsNoteView boatId={boatId} types={types} onDismiss={dismiss} dismissing={dismissing} />
  );
}
